<template>
  <Modal
    size="xl"
    @close="$emit('close')"
  >
    <template #header>
      <div class="w-full text-center">
        <h1 class="text-2xl">
          {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.title') }}
        </h1>
      </div>
    </template>

    <template #body>
      <div class="flex flex-col h-full w-full overflow-y-auto">
        <div v-if="fetchCorrespondencesAction.is(States.COMPLETE)">
          <div class="flex justify-between items-center">
            <div class="font-semibold text-lg w-1/2 text-wrap">
              {{ variationOrder.code ? variationOrder.code + ' - ' : '' }} {{ variationOrder.description }}
            </div>

            <div
              v-if="projectsStore.project?.unarchived"
              class="flex space-x-4 items-center"
            >
              <button
                type="button"
                class="btn btn--secondary-blue"
                @click="showForm = !showForm"
              >
                {{ showForm ? $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.showCorrespondence') : $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.addCorrespondence') }}
              </button>
            </div>
          </div>

          <div
            v-if="showForm"
            class="flex justify-center"
          >
            <form
              action=""
              class="flex flex-col space-y-4 p-2 w-2/3 mx-auto"
              @submit.prevent="saveCorrespondence"
            >
              <label>
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.dateReceived') }}

                <AppInput
                  v-model="correspondenceForm.form.date_received"
                  name="date_received"
                  type="date"
                  class="form-input"
                  :error="correspondenceForm.getErrors('date_received')"
                />
              </label>

              <label>
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.to') }}

                <AppInput
                  v-model="correspondenceForm.form.to"
                  name="to"
                  type="text"
                  class="form-input"
                  :error="correspondenceForm.getErrors('to')"
                />
              </label>

              <label>
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.from') }}

                <AppInput
                  v-model="correspondenceForm.form.from"
                  name="from"
                  class="form-input"
                  type="text"
                  :error="correspondenceForm.getErrors('from')"
                />
              </label>

              <label>
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.description') }}

                <textarea
                  v-model="correspondenceForm.form.description"
                  name="description"
                  class="form-input resize-none"
                  type="text"
                />

                <span
                  v-if="correspondenceForm.getErrors('description')"
                  class="text-red-600 text-sm"
                >{{ correspondenceForm.getErrors('description') }}</span>
              </label>

              <label class="flex justify-between w-full">
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.responseRequired') }}

                <input
                  v-model="correspondenceForm.form.response_required"
                  name="response_required"
                  type="checkbox"
                  class="form-checkbox"
                  true-value="1"
                  false-value="0"
                >
              </label>

              <div>
                {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.label.correspondenceWas') }}
                <div class="flex items-center mx-auto space-x-4">
                  <button
                    type="button"
                    class="btn w-1/2"
                    :class="[correspondenceForm.form.is_issued === 0 ? 'btn--primary-blue' : 'btn--secondary-blue']"
                    data-test="type-internal"
                    @click="correspondenceForm.form.is_issued = 0"
                  >
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.received') }}
                  </button>

                  <button
                    type="button"
                    class="btn w-1/2"
                    :class="[correspondenceForm.form.is_issued === 1 ? 'btn--primary-blue' : 'btn--secondary-blue']"
                    data-test="type-external"
                    @click="correspondenceForm.form.is_issued = 1"
                  >
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.issued') }}
                  </button>
                </div>
              </div>

              <div
                class="h-20 w-full"
              >
                <FileUpload
                  v-model="correspondenceForm.form.media"
                  multiple
                  @change="addFile"
                />
              </div>

              <div
                v-if="filesUploaded.length"
                class="overflow-x-auto flex"
              >
                <UploadedFileDisplay
                  v-for="(file, i) in filesUploaded"
                  :key="`${file.fileName}-${i}`"
                  :file="file"
                  :error="correspondenceForm.getErrors(`media.${i}`)"
                  @delete="removeFile(i)"
                />
              </div>
            </form>
          </div>

          <div
            v-else
            class=""
          >
            <table
              v-if="correspondences.length > 0"
              class="w-full mt-4"
            >
              <thead>
                <tr class="text-left child:p-2 child:border-y first-child:border-l last-child:border-r child:border-gray-300">
                  <th>
                    <div>
                      {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.description') }}&nbsp;

                      <button
                        type="button"
                        class="text-gray-300 text-sm"
                        @click="sortColumn = sortColumn === 'type' ? 'date_received' : 'type'"
                      >
                        {{ sortColumn === 'type' ? $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.sortByDate') : $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.sortByType') }}
                      </button>
                    </div>
                  </th>

                  <th>
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.to') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.from') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.issuedReceived') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.date') }}
                  </th>

                  <th>
                    {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.responseRequired') }}
                  </th>
                </tr>
              </thead>

              <tbody>
                <CorrespondenceRow
                  v-for="correspondence in correspondences"
                  :key="correspondence.id"
                  :correspondence="correspondence"
                />
              </tbody>
            </table>

            <p
              v-else
              class="font-semibold text-center m-4"
            >
              {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.table.noCorrespondencesFound') }}
            </p>
          </div>
        </div>

        <AppSpinner
          v-else
          class="mx-auto"
        />
      </div>
    </template>

    <template #footer>
      <div class="w-full flex justify-end space-x-4">
        <button
          type="button"
          class="btn btn--secondary-blue"
          @click="$emit('close');"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.close') }}
        </button>

        <button
          v-if="showForm"
          type="button"
          class="btn btn--primary-blue"
          @click="saveCorrespondence"
        >
          {{ $t('HeadContract.VariationsSchedule.VariationCorrespondenceModal.button.save') }}
        </button>
      </div>
    </template>
  </Modal>
</template>

<script setup lang="ts">
import FileUpload from '@/components/FileUpload.vue';
import Modal from '@/components/Modal.vue';
import UploadedFileDisplay from '@/components/UploadedFileDisplay.vue';
import { useFileUploadForm } from '@/composables/useFileUploadForm';
import { useFormData } from '@/composables/useFormData';
import { States, useStoreApiAction } from '@/composables/useStoreApiAction';
import { dateForDateInput, falsey } from '@/helpers';
import VariationOrder, { VariationOrderCorrespondenceTypes } from '@/models/VariationOrder';
import { useProjectsStore } from '@/store/projects';
import { useVariationOrdersStore } from '@/store/variationOrders';
import Swal from 'sweetalert2';
import { computed, ref, watch } from 'vue';
import CorrespondenceRow from './CorrespondenceRow.vue';

const props = defineProps<{
  variationOrderId: Id;
}>();

const emits = defineEmits<{
  (e: 'close');
}>();

const variationOrdersStore = useVariationOrdersStore();
const sortColumn = ref<'type' | 'date_received'>('type');

const variationOrder = computed(() => {
  return variationOrdersStore.models
    .with('trade_items')
    .with('user')
    .with('correspondences', (query) => {
      query.orderBy('date_received', 'desc');
    })
    .where(
      'id',
      parseInt(props.variationOrderId as string),
    )
    .first();
});

const correspondences = computed(() => {
  let variationCorrespondences = [];

  switch (sortColumn.value) {
    case 'type':
      variationCorrespondences = variationOrder.value.correspondences.toSorted((a, b) => {
        if(!falsey(a.type) && !falsey(b.type)) {
          return (VariationOrderCorrespondenceTypes[b.type] ?? 0) - (VariationOrderCorrespondenceTypes[a.type] ?? 0);
        } else if(falsey(a.type) && !falsey(b.type)) {
          return 1;
        }

        return -1;
      });

      break;

    case 'date_received':
      variationCorrespondences = variationOrder.value.correspondences;

      break;
  }

  return variationCorrespondences;
});

const fetchCorrespondencesAction = useStoreApiAction(variationOrdersStore.fetchCorrespondences);
const storeCorrespondenceAction = useStoreApiAction(variationOrdersStore.storeCorrespondence);

watch(() => props.variationOrderId, (newValue) => {
  if(newValue) {
    fetchCorrespondencesAction.request(newValue);
  }
}, { immediate: true });

const showForm = ref(false);

const correspondenceForm = useFormData({
  date_received: dateForDateInput(new Date()),
  to: '',
  from: '',
  description: '',
  response_required: 0,
  is_issued: 0,
  media: [],
});

const { filesUploaded, addFile, removeFile } = useFileUploadForm(undefined, (file, index) => {
  correspondenceForm.form.media.splice(index, 1);
});

const saveCorrespondence = () => {
  storeCorrespondenceAction.request(props.variationOrderId, correspondenceForm.toFormDataObject()).then(() => {
    Swal.fire({
      icon: 'success',
      text: 'Correspondence Saved',
    }).then(() => {
      emits('close');
    });

    correspondenceForm.resetData();
    filesUploaded.value = [];
  }).catch((error) => {
    correspondenceForm.setErrors(error.data);
  });
};

const projectsStore = useProjectsStore();
</script>

<style scoped></style>
